/**
 * A timeline section — the curator's view of an owner's `history` array.
 *
 * Shared by the manufacturer editor and the brand editor, which want the same
 * thing: the entities differ in what they have a history OF, not in how it is
 * stored or edited. Owner-specific behaviour arrives as `TimelineOwner`
 * (writer, uploader, storage prefix, enrichment callable), so this component
 * knows nothing about either.
 *
 * SHAPE follows `CalibreTiersEditor`: compact rows, each opening a full editor
 * in a modal, rather than a column of inline forms behind a collapse toggle.
 *
 * PERSISTENCE deliberately does NOT follow it. That component says in its own
 * docstring that it "never writes to Firestore itself", leaving its array to
 * the calibre modal's save. Neither owner's form draft carries `history`, so
 * the same arrangement here is precisely what lost a curator's event: the
 * top-level Save wrote every other field and silently dropped the timeline.
 * Every write is transactional — the event modal's Save writes, Delete writes,
 * and the enrichment dialog's Merge & Save writes. There is no section Save
 * button, no buffered draft, and so nothing for a close to discard.
 *
 * One writer, though. The modal and the dialog hand their results here and this
 * component performs the write, so two copies of the array can never race.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import type { ReactNode } from 'react';
import type { TimelineEvent, TimelineEventImage } from '@watchkeeper/shared-types';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import {
  deleteCalibreImage,
  getImageUrl,
  type AIHistoryResult,
  type AIProposedTimelineEvent,
} from '../../services/firebase';
import { TimelineEnrichDialog, type TimelineApplyMode } from './TimelineEnrichDialog';
import { TimelineEventModal } from './TimelineEventModal';
import { TimelineHistoryHero } from './TimelineHistoryHero';
import {
  parseTimelineYear,
  sortTimelineEvents,
  timelineCategoryLabel,
  timelineForWrite,
} from '../../utils/timeline';

export interface TimelineOwner {
  /** Which entity owns this timeline — namespaces the enrichment cache. */
  kind: 'brand' | 'manufacturer' | 'parentOrg';
  id: string;
  /** Shown in the dialog title and the save toast. */
  name: string;
  history?: TimelineEvent[];
  /**
   * Where this owner's timeline images live, e.g. `brands/rolex/timeline/`.
   * Drives the delete-safety check — bytes are only ever removed under it.
   */
  storagePrefix: string;
  /** Writes the whole `history` array for this owner. */
  persist: (history: TimelineEvent[]) => Promise<void>;
  /** Uploads bytes for this owner and returns the storage path. */
  uploadImage: (file: File | Blob, fileName?: string) => Promise<string>;
  /**
   * Runs this owner's enrichment callable. Optional: an owner with no history
   * prompt yet (parent organizations, until `enrichParentOrgHistory` exists)
   * leaves it out, and the ✨ AI Enrich button is simply not offered — rather
   * than offered and wired to nothing.
   */
  enrich?: (input: {
    additionalContext?: string[];
    sourceUrlContent?: string;
  }) => Promise<AIHistoryResult>;

  /** The banner leading this owner's history screen, if a curator set one. */
  historyHeroImage?: TimelineEventImage;
  /**
   * Where the HERO's bytes live, e.g. `brands/rolex/history-hero/`. Its own
   * prefix, deliberately not `storagePrefix`: the hero deletes and replaces its
   * own file, and sharing a prefix with the event images would let one delete
   * take out the other's picture.
   */
  heroStoragePrefix: string;
  /** Uploads the hero's bytes for this owner and returns the storage path. */
  uploadHeroImage: (file: File | Blob, fileName?: string) => Promise<string>;
  /** Writes `historyHeroImage`. `null` clears it — see the wrappers for why. */
  persistHeroImage: (image: TimelineEventImage | null) => Promise<void>;
}

interface Props {
  owner: TimelineOwner;
  /**
   * Rendered under the header — for a control that belongs beside the timeline
   * but is not part of it. Exists so `hideTimelineFromPrimaryBrand`, which is a
   * manufacturer-only concept controlling the manufacturer→primary-brand
   * cascade, can sit here without this component knowing what a manufacturer is.
   */
  headerExtra?: ReactNode;
}

/** Which editor is open: null = none, 'new' = create, otherwise the row index. */
type EditorTarget = null | 'new' | number;

export function TimelineSection({ owner, headerExtra }: Props) {
  const events = useMemo(() => sortTimelineEvents(owner.history ?? []), [owner.history]);

  const [editing, setEditing] = useState<EditorTarget>(null);
  const [enrichOpen, setEnrichOpen] = useState(false);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  /**
   * Write a whole timeline through the owner's writer.
   *
   * Takes the array rather than reading state, so a caller that has just
   * computed the next timeline (the AI dialog's merge) persists what it built
   * rather than the previous render's copy.
   *
   * Rethrows: the event modal keeps its draft on screen when a write fails,
   * which is only possible if the failure reaches it.
   */
  const persist = useCallback(
    async (next: TimelineEvent[]) => {
      const history = timelineForWrite(next);
      await owner.persist(history);
      return history;
    },
    [owner],
  );

  const handleSaveEvent = useCallback(
    async (event: TimelineEvent) => {
      const next =
        editing === 'new' ? [...events, event] : events.map((e, i) => (i === editing ? event : e));
      try {
        await persist(next);
        setEditing(null);
        toast.success(editing === 'new' ? 'Event added.' : 'Event saved.');
      } catch (err) {
        console.error('Failed to save timeline event:', err);
        toast.error('Could not save that event.');
        throw err;
      }
    },
    [editing, events, persist],
  );

  const handleDelete = useCallback(
    async (index: number) => {
      setBusy(true);
      try {
        const victim = events[index];
        await persist(events.filter((_, i) => i !== index));
        setDeleting(null);
        // Only after the document no longer references it, and only when this
        // manufacturer owns the bytes — a pasted foreign URL is somebody else's
        // file. Deleting first would strand the row on a missing image if the
        // write then failed.
        const path = victim?.image?.storagePath;
        if (path?.startsWith(owner.storagePrefix)) {
          try {
            await deleteCalibreImage(path);
          } catch (err) {
            console.warn('Timeline image delete failed (non-fatal):', err);
          }
        }
        toast.success('Event deleted.');
      } catch (err) {
        console.error('Failed to delete timeline event:', err);
        toast.error('Could not delete that event.');
      } finally {
        setBusy(false);
      }
    },
    [events, persist, owner.storagePrefix],
  );

  /** Fold AI events in and write, in one action — the dialog's Merge & Save. */
  const applyEnrichment = useCallback(
    async (proposed: AIProposedTimelineEvent[], mode: TimelineApplyMode) => {
      const incoming: TimelineEvent[] = proposed.map((e) => ({
        date: e.date,
        title: e.title,
        description: e.description,
        ...(e.category ? { category: e.category as TimelineEvent['category'] } : {}),
        ...(e.sourceUrl ? { sourceUrl: e.sourceUrl } : {}),
        ...(e.image ? { image: e.image } : {}),
        aiConfidence: e.aiConfidence,
      }));
      const history = await persist(mode === 'replace' ? incoming : [...events, ...incoming]);
      toast.success(
        `${incoming.length} event${incoming.length === 1 ? '' : 's'} added to ${
          owner.name
        }’s history — ${history.length} on the timeline.`,
      );
    },
    [events, persist, owner.name],
  );

  const undatedCount = events.filter((e) => parseTimelineYear(e.date) === Infinity).length;
  const editingEvent = typeof editing === 'number' ? (events[editing] ?? null) : null;

  return (
    <div className="border-t pt-4">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-gray-900">Timeline</h3>
        <span className="text-[11px] text-gray-500">
          {events.length} event{events.length === 1 ? '' : 's'}
        </span>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => setEditing('new')} disabled={busy}>
            + Add event
          </Button>
          {owner.enrich && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setEnrichOpen(true)}
              disabled={busy}
              title="Propose a timeline with AI, review it, and save in one step."
            >
              ✨ AI Enrich
            </Button>
          )}
        </div>
      </div>

      <p className="mt-1 text-[11px] text-gray-500">
        Ordered by the first four-digit year in each date, which is free text.
        {undatedCount > 0 && (
          <span className="text-amber-700">
            {' '}
            {undatedCount} without a readable year {undatedCount === 1 ? 'sorts' : 'sort'} to the
            end.
          </span>
        )}
      </p>

      {headerExtra}

      <TimelineHistoryHero
        value={owner.historyHeroImage}
        storagePrefix={owner.heroStoragePrefix}
        uploadImage={owner.uploadHeroImage}
        persist={owner.persistHeroImage}
      />

      {events.length === 0 ? (
        <p className="mt-3 text-xs text-gray-400">
          No events yet. Add the founding, the milestones and the closure
          {owner.enrich ? ' — or let AI propose a timeline to review.' : '.'}
        </p>
      ) : (
        <ul className="mt-3 divide-y divide-gray-100 border border-gray-200 rounded-lg">
          {events.map((event, i) => (
            <TimelineRow
              key={i}
              event={event}
              disabled={busy}
              onEdit={() => setEditing(i)}
              onDelete={() => setDeleting(i)}
            />
          ))}
        </ul>
      )}

      <TimelineEventModal
        isOpen={editing !== null}
        storagePrefix={owner.storagePrefix}
        uploadImage={owner.uploadImage}
        initial={editingEvent}
        onCancel={() => setEditing(null)}
        onSave={handleSaveEvent}
      />

      {owner.enrich && (
        <TimelineEnrichDialog
          isOpen={enrichOpen}
          ownerKind={owner.kind}
          ownerId={owner.id}
          ownerName={owner.name}
          enrich={owner.enrich}
          existing={events}
          onClose={() => setEnrichOpen(false)}
          onApply={applyEnrichment}
        />
      )}

      <Modal
        isOpen={deleting !== null}
        onClose={() => (busy ? undefined : setDeleting(null))}
        title="Delete timeline event"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            Delete{' '}
            <span className="font-medium">
              {deleting !== null ? events[deleting]?.title || 'this event' : ''}
            </span>
            ? Any image attached to it is deleted too.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDeleting(null)} disabled={busy} autoFocus>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={busy}
              disabled={busy}
              onClick={() => deleting !== null && void handleDelete(deleting)}
            >
              Delete
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

/** One compact row: thumbnail, date, title, badges, actions. */
function TimelineRow({
  event,
  disabled,
  onEdit,
  onDelete,
}: {
  event: TimelineEvent;
  disabled: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const [thumb, setThumb] = useState<string | null>(null);

  // Storage paths are not renderable, and a tokenless URL under a non-public
  // prefix 403s — re-sign for display.
  useEffect(() => {
    let cancelled = false;
    if (event.image?.storagePath) {
      void getImageUrl(event.image.storagePath)
        .then((url) => !cancelled && setThumb(url))
        .catch(() => !cancelled && setThumb(null));
    } else {
      setThumb(event.image?.url ?? null);
    }
    return () => {
      cancelled = true;
    };
  }, [event.image?.storagePath, event.image?.url]);

  const needsImage = !!event.image?.aiSuggestion && !event.image.storagePath && !event.image.url;

  return (
    <li className="flex items-center gap-3 px-3 py-2">
      <div className="w-10 h-10 shrink-0 rounded bg-gray-100 border border-gray-200 overflow-hidden flex items-center justify-center">
        {thumb ? (
          <img src={thumb} alt="" className="w-full h-full object-cover" />
        ) : (
          <span className="text-[9px] text-gray-300">—</span>
        )}
      </div>
      <span className="font-mono text-xs text-gray-500 w-24 shrink-0">{event.date || '—'}</span>
      <span className="min-w-0 flex-1 truncate text-sm text-gray-900">
        {event.title || <span className="text-gray-400">Untitled</span>}
      </span>
      {needsImage && (
        <span
          className="rounded-full bg-amber-100 px-1.5 py-px text-[9px] font-medium uppercase tracking-wide text-amber-800"
          title={event.image?.aiSuggestion}
        >
          needs image
        </span>
      )}
      {event.category && (
        <span className="rounded bg-gray-100 px-1.5 py-px text-[10px] uppercase tracking-wide text-gray-600">
          {timelineCategoryLabel(event.category)}
        </span>
      )}
      <button
        type="button"
        onClick={onEdit}
        disabled={disabled}
        className="text-xs text-indigo-600 hover:underline disabled:text-gray-300"
        aria-label={`Edit ${event.title || 'event'}`}
      >
        Edit
      </button>
      <button
        type="button"
        onClick={onDelete}
        disabled={disabled}
        className="text-xs text-red-600 hover:underline disabled:text-gray-300"
        aria-label={`Delete ${event.title || 'event'}`}
      >
        Delete
      </button>
    </li>
  );
}
